(function () {
  const SECTION_SELECTOR = '[data-section-type="main-collection-product-grid"]';
  const GRID_SELECTOR = '#product-grid';
  const GRID_CONTAINER_SELECTOR = '#ProductGridContainer';
  const BANNER_CLASS = 'collection-json-banner-item';

  let isInjectingBanners = false;
  let gridObserver = null;
  let containerObserver = null;

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function getSection(scope = document) {
    if (scope instanceof Element && scope.matches(SECTION_SELECTOR)) {
      return scope;
    }

    if (scope instanceof Element) {
      return scope.querySelector(SECTION_SELECTOR);
    }

    return document.querySelector(SECTION_SELECTOR);
  }

  function getBannerData(section) {
    if (!section) return null;

    const rawData = section.getAttribute('data-collection-banners');
    if (!rawData) return null;

    try {
      return JSON.parse(rawData);
    } catch (error) {
      console.error('Invalid collection banner JSON:', error);
      return null;
    }
  }

  function getGrid(section) {
    if (!section) return null;
    return section.querySelector(GRID_SELECTOR);
  }

  function getGridContainer(section) {
    if (!section) return null;
    return section.querySelector(GRID_CONTAINER_SELECTOR);
  }

  function getProductItems(grid) {
    if (!grid) return [];

    return Array.from(grid.children).filter((item) => {
      return (
        item.nodeType === 1 &&
        item.matches('li.grid__item') &&
        !item.classList.contains(BANNER_CLASS)
      );
    });
  }

  function removeExistingBanners(grid) {
    if (!grid) return;

    grid.querySelectorAll(`.${BANNER_CLASS}`).forEach((banner) => {
      banner.remove();
    });
  }

  function buildMediaHTML(banner) {
    if (banner.video) {
      return `
        <video
          src="${escapeHtml(banner.video)}"
          autoplay
          muted
          loop
          playsinline
          preload="metadata"
        ></video>
      `;
    }

    if (banner.image) {
      return `
        <img
          src="${escapeHtml(banner.image)}"
          alt="${escapeHtml(banner.alt || '')}"
          loading="lazy"
        >
      `;
    }

    return '';
  }

  function buildBannerNode(banner, index, animationOrder) {
    const mediaHTML = buildMediaHTML(banner);
    if (!mediaHTML) return null;

    const hasLink = !!banner.link;
    const openNewTab = !!banner.new_tab;

    const contentHTML = hasLink
      ? `<a href="${escapeHtml(banner.link)}" ${
          openNewTab ? 'target="_blank" rel="noopener noreferrer"' : ''
        }>${mediaHTML}</a>`
      : mediaHTML;

    const li = document.createElement('li');
    li.className = `grid__item ${BANNER_CLASS} ${BANNER_CLASS}--${index + 1}`;
    li.setAttribute('data-banner-index', String(index));
    li.setAttribute('data-banner-position', String(banner.position));

    if (animationOrder) {
      li.classList.add('scroll-trigger', 'animate--slide-in');
      li.setAttribute('data-cascade', '');
      li.style.setProperty('--animation-order', String(animationOrder));
    }

    li.innerHTML = `
      <div class="collection-json-banner-media">
        ${contentHTML}
      </div>
    `;

    return li;
  }

  function injectCollectionBanners(scope = document) {
    if (isInjectingBanners) return false;

    const section = getSection(scope);
    if (!section) return false;

    const data = getBannerData(section);
    const banners = data?.banners || [];
    if (!banners.length) return false;

    const grid = getGrid(section);
    if (!grid) return false;

    const productItems = getProductItems(grid);
    if (!productItems.length) return false;

    isInjectingBanners = true;

    try {
      removeExistingBanners(grid);

      banners.forEach((banner, index) => {
        if (!banner || !banner.position) return;

        const positionIndex = Number(banner.position) - 1;
        if (Number.isNaN(positionIndex) || positionIndex < 0) return;
        if (positionIndex >= productItems.length) return;

        const referenceItem = productItems[positionIndex];
        if (!referenceItem) return;

        let animationOrder = null;
        const referenceAnimationOrder = referenceItem.style.getPropertyValue('--animation-order');

        if (referenceAnimationOrder) {
          animationOrder = referenceAnimationOrder;
        }

        const bannerNode = buildBannerNode(banner, index, animationOrder);
        if (!bannerNode) return;

        referenceItem.insertAdjacentElement('afterend', bannerNode);
      });
    } finally {
      window.requestAnimationFrame(() => {
        isInjectingBanners = false;
      });
    }

    return true;
  }

  function debounce(fn, wait) {
    let timeout;

    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  const refreshBanners = debounce((scope = document) => {
    injectCollectionBanners(scope);
    observeGrid(scope);
  }, 80);

  function observeGrid(scope = document) {
    const section = getSection(scope);
    if (!section) return;

    const grid = getGrid(section);
    if (!grid) return;

    if (gridObserver) {
      gridObserver.disconnect();
    }

    gridObserver = new MutationObserver((mutations) => {
      if (isInjectingBanners) return;

      let shouldRefresh = false;

      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;

        const hasNonBannerAddedNodes = Array.from(mutation.addedNodes).some((node) => {
          return (
            node.nodeType === 1 &&
            node.matches &&
            !node.matches(`.${BANNER_CLASS}`)
          );
        });

        const hasNonBannerRemovedNodes = Array.from(mutation.removedNodes).some((node) => {
          return (
            node.nodeType === 1 &&
            node.matches &&
            !node.matches(`.${BANNER_CLASS}`)
          );
        });

        if (hasNonBannerAddedNodes || hasNonBannerRemovedNodes) {
          shouldRefresh = true;
          break;
        }
      }

      if (shouldRefresh) {
        refreshBanners(section);
      }
    });

    gridObserver.observe(grid, {
      childList: true
    });
  }

  function observeGridContainer(scope = document) {
    const section = getSection(scope);
    if (!section) return;

    const container = getGridContainer(section);
    if (!container) return;

    if (containerObserver) {
      containerObserver.disconnect();
    }

    containerObserver = new MutationObserver((mutations) => {
      if (isInjectingBanners) return;

      let productGridWasReplaced = false;

      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;

        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];

        const foundGridChange = nodes.some((node) => {
          return (
            node.nodeType === 1 &&
            (
              (node.id && node.id === 'product-grid') ||
              (node.querySelector && node.querySelector('#product-grid'))
            )
          );
        });

        if (foundGridChange) {
          productGridWasReplaced = true;
          break;
        }
      }

      if (productGridWasReplaced) {
        refreshBanners(section);
      }
    });

    containerObserver.observe(container, {
      childList: true,
      subtree: true
    });
  }

  function init(scope = document) {
    injectCollectionBanners(scope);
    observeGridContainer(scope);
    observeGrid(scope);
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();